/**
 * SalesStudio.js
 * One place for every value piece that rides along inside a cold email.
 *
 * Top: "Your bots" — everything already built, across all play types, in one
 * list. Clicking a play opens its editor directly.
 * Below: the Bot Store — every kind of play that exists (or is coming).
 *
 * Plays are stored as Image Lab assets (`/World/{uid}/settings/imageLab`);
 * Website Redesigner pages share a `playGroupId` and show up as one play.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ref, onValue } from "firebase/database";
import { database } from "../firebaseConfig";
import ImageLab from "./ImageLab";
import BrandPlayEditor from "./BrandPlayEditor";
import MiniAppEditor from "./MiniAppEditor";
import { SALES_PLAY_CONFIGS } from "./salesPlayConfigs";
import useResearchAccess from "../hooks/useResearchAccess";
import { PLAN_NAV_BADGE } from "../config/creditPlans";
import "./SalesStudio.css";

// Newest first.
const STORE_PLAYS = [
  {
    id: "miniApp",
    name: "Mini-App",
    kicker: "A small app for one prospect",
    description:
      "Describe it once: an ROI calculator, a website comparison, a configurator. The AI builds each contact their own interactive app from their research, sent as a clickable card in the email.",
    cover: "/img/sales-studio/store/miniApp.webp",
    icon: "fa-calculator",
    glyph: "/img/sales-studio/icons/miniApp.webp",
    status: "new",
    cta: "Create Mini App",
    open: { kind: "miniApp", assetId: null },
  },
  {
    id: "contentPieces",
    name: "Content Creator",
    kicker: "Posts & ads in their brand",
    description:
      "Reads the prospect's brand from their website and creates up to three ready-to-post pieces: Instagram posts and stories, Meta and Google ads, LinkedIn visuals, flyers or vouchers.",
    cover: "/img/sales-studio/store/contentPieces.webp",
    icon: "fa-photo-film",
    glyph: "/img/sales-studio/icons/contentPieces.webp",
    status: "live",
    cta: "Create Content Creator",
    open: { kind: "play", playType: "contentPieces", groupId: null },
  },
  {
    id: "funnyGif",
    name: "Funny GIF",
    kicker: "4 seconds they will remember",
    description:
      "Reads what the prospect does and casts it in a short looping gag: their product dancing, lifting off, saving the day. Rendered small and cheap, it plays right inside the email.",
    cover: "/img/sales-studio/store/funnyGif.webp",
    icon: "fa-face-laugh-squint",
    glyph: "/img/sales-studio/icons/funnyGif.webp",
    status: "new",
    cta: "Create Funny GIF",
    open: { kind: "play", playType: "funnyGif", groupId: null },
  },
  {
    id: "photoUpgrade",
    name: "Photo Retoucher",
    kicker: "Their own photos, pro-level",
    description:
      "Finds the right real photo on their website (hotel room, dish, product, listing, team) and upgrades it. Before / after, so they see the difference instantly.",
    image: "/img/image_create_1.webp",
    cover: "/img/sales-studio/store/photoUpgrade.webp",
    icon: "fa-camera-retro",
    glyph: "/img/sales-studio/icons/photoUpgrade.webp",
    status: "new",
    cta: "Create Photo Retoucher",
    open: { kind: "play", playType: "photoUpgrade", groupId: null },
  },
  {
    id: "websiteRedesign",
    name: "Website Redesigner",
    kicker: "Their site, but better",
    description:
      "Studies the prospect's website, logo and branding, then shows up to three of their pages redesigned. Homepage, booking calendar, services: you pick.",
    image: "/img/image_create_2.webp",
    cover: "/img/sales-studio/store/websiteRedesign.webp",
    icon: "fa-window-restore",
    glyph: "/img/sales-studio/icons/websiteRedesign.webp",
    status: "live",
    cta: "Create Website Redesigner",
    open: { kind: "play", playType: "websiteRedesign", groupId: null },
  },
  {
    id: "analyses",
    name: "Analysis Designer",
    kicker: "Their numbers, as a graphic",
    description:
      "Turns data you already have on a contact into a chart: visibility, competitors, traffic value, ROI. When a figure has to be calculated, a stronger model does the math from their data.",
    cover: "/img/sales-studio/store/analyses.webp",
    icon: "fa-chart-line",
    glyph: "/img/sales-studio/icons/analyses.webp",
    status: "new",
    cta: "Create Analysis Designer",
    open: { kind: "play", playType: "analyses", groupId: null },
  },
  {
    id: "aiImage",
    name: "AI Image",
    kicker: "One prompt, every contact",
    description:
      "Describe it once. A unique image is rendered for each contact from their own data: charts, local heroes, competitor gaps.",
    cover: "/img/sales-studio/store/imageLab.webp",
    icon: "fa-wand-magic-sparkles",
    glyph: "/img/sales-studio/icons/aiImage.webp",
    status: "live",
    cta: "Create",
    open: { kind: "lab", tool: "ai" },
  },
  {
    id: "staticImage",
    name: "Image",
    kicker: "Upload once, use everywhere",
    description:
      "A single image you upload and drop into any email with a tag. It travels inline, so it never breaks.",
    cover: "/img/sales-studio/store/staticImage.webp",
    icon: "fa-image",
    glyph: "/img/sales-studio/icons/staticImage.webp",
    status: "live",
    cta: "Create",
    open: { kind: "lab", tool: "image" },
  },
  {
    id: "screenComposer",
    name: "Screen Composer",
    kicker: "Their site, captured live",
    description:
      "A screenshot or scroll GIF of the contact's website, generated for each recipient and placed in the email.",
    cover: "/img/sales-studio/store/screenComposer.webp",
    icon: "fa-display",
    glyph: "/img/sales-studio/icons/screenComposer.webp",
    status: "live",
    cta: "Open",
    open: { kind: "lab", tool: "screen" },
  },
  {
    id: "pdfReport",
    name: "PDF Audit Report",
    kicker: "One-page analysis",
    description:
      "A branded PDF with website, SEO and tech findings for this exact company, linked straight from the email.",
    image: "/img/docu_sales.webp",
    icon: "fa-file-pdf",
    status: "soon",
  },
  {
    id: "deckGenerator",
    name: "Pitch Deck Generator",
    kicker: "Slides built for one lead",
    description:
      "A short presentation with their logo, their numbers and your offer mapped onto their business.",
    icon: "fa-person-chalkboard",
    status: "soon",
  },
];

const LAB_NAMES = { ai: "AI Image", image: "Image", screen: "Screen Composer" };

const SHOW_COMING_SOON = false;
const VISIBLE_STORE_PLAYS = STORE_PLAYS.filter((p) => SHOW_COMING_SOON || p.status !== "soon");

const KIND_META = {
  miniApp: { label: "Mini-App", icon: "fa-calculator" },
  websiteRedesign: { label: "Website Redesigner", icon: "fa-window-restore" },
  contentPieces: { label: "Content Creator", icon: "fa-photo-film" },
  photoUpgrade: { label: "Photo Retoucher", icon: "fa-camera-retro" },
  analyses: { label: "Analysis Designer", icon: "fa-chart-line" },
  funnyGif: { label: "Funny GIF", icon: "fa-face-laugh-squint" },
  ai: { label: "AI Image", icon: "fa-wand-magic-sparkles" },
  static: { label: "Image", icon: "fa-image" },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "miniApp", label: "Mini-Apps" },
  { id: "websiteRedesign", label: "Website Redesigner" },
  { id: "contentPieces", label: "Content Creator" },
  { id: "photoUpgrade", label: "Photo Retoucher" },
  { id: "analyses", label: "Analysis Designer" },
  { id: "funnyGif", label: "Funny GIFs" },
  { id: "ai", label: "AI Images" },
  { id: "static", label: "Images" },
];

const assetThumb = (a) => a.previewImageUrl || a.imageUrl || a.baseImageUrl || "";

// A tab closed mid-render never clears the flag, so it expires.
const PREVIEW_PENDING_TTL_MS = 5 * 60 * 1000;
const isPreviewPending = (a) =>
  !!a.previewPending && Date.now() - (a.updatedAt || a.createdAt || 0) < PREVIEW_PENDING_TTL_MS;

const buildPlays = (assets) => {
  const groups = new Map();
  const plays = [];
  assets.forEach((a) => {
    if (SALES_PLAY_CONFIGS[a.play] && a.playGroupId) {
      if (!groups.has(a.playGroupId)) groups.set(a.playGroupId, []);
      groups.get(a.playGroupId).push(a);
      return;
    }
    plays.push({
      kind: a.type === "miniApp" ? "miniApp" : a.type === "ai" ? "ai" : "static",
      id: a.id,
      name: a.name || "Untitled",
      pages: [{ id: a.id, thumb: assetThumb(a), pending: isPreviewPending(a), label: null }],
      tokens: a.token ? [a.token] : [],
      sortAt: a.updatedAt || a.createdAt || 0,
    });
  });
  groups.forEach((pages, groupId) => {
    const sorted = [...pages].sort((a, b) => (a.pageOrder || 0) - (b.pageOrder || 0));
    const config = SALES_PLAY_CONFIGS[sorted[0].play];
    plays.push({
      kind: config.id,
      id: groupId,
      name: sorted[0].playName || config.defaultName,
      pages: sorted.map((p) => ({ id: p.id, thumb: assetThumb(p), pending: isPreviewPending(p), label: p.pageLabel })),
      tokens: sorted.map((p) => p.token).filter(Boolean),
      sortAt: Math.max(...sorted.map((p) => p.updatedAt || p.createdAt || 0)),
    });
  });
  return plays.sort((a, b) => b.sortAt - a.sortAt);
};

const Art = ({ src, icon, className }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <i className={`fa-solid ${icon} ${className}`} />;
  return <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
};

const PlayThumb = ({ page, icon }) => (
  <div className="ssThumb">
    {page.pending ? (
      <div className="ssThumbGen">
        <i className="fa-solid fa-wand-magic-sparkles fa-fade" />
        <span>Creating preview…</span>
      </div>
    ) : page.thumb ? (
      <img src={page.thumb} alt="" loading="lazy" />
    ) : (
      <i className={`fa-solid ${icon} ssThumbEmpty`} />
    )}
    {page.label && <span className="ssThumbLabel">{page.label}</span>}
  </div>
);

const PlayCard = ({ play, onOpen }) => {
  const [copied, setCopied] = useState("");
  const meta = KIND_META[play.kind];

  const copy = (event, token) => {
    event.stopPropagation();
    try {
      navigator.clipboard.writeText(`{{image:${token}}}`);
      setCopied(token);
      setTimeout(() => setCopied(""), 1600);
    } catch (_) {
      /* clipboard unavailable */
    }
  };

  return (
    <div
      className="ssPlay"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className={`ssPlayThumbs ssPlayThumbs--${Math.min(play.pages.length, 3)}`}>
        {play.pages.slice(0, 3).map((page) => (
          <PlayThumb key={page.id} page={page} icon={meta.icon} />
        ))}
      </div>
      <div className="ssPlayBody">
        <span className={`ssKind ssKind--${play.kind}`}>
          <i className={`fa-solid ${meta.icon}`} />
          {meta.label}
        </span>
        <span className="ssPlayName" title={play.name}>{play.name}</span>
        <div className="ssPlayTags">
          {play.tokens.map((token) => (
            <button
              type="button"
              key={token}
              className={`ssTag ${copied === token ? "copied" : ""}`}
              onClick={(e) => copy(e, token)}
              title="Copy tag"
            >
              <code>{`{{image:${token}}}`}</code>
              <i className={copied === token ? "fa-solid fa-check" : "fa-regular fa-copy"} />
            </button>
          ))}
        </div>
      </div>
      <i className="fa-solid fa-arrow-right ssPlayArrow" />
    </div>
  );
};

const LAB_KINDS = { ai: "ai", image: "static" };
const storeKind = (item) =>
  item.open?.kind === "play"
    ? item.open.playType
    : item.open?.kind === "lab"
    ? LAB_KINDS[item.open.tool]
    : item.open?.kind === "miniApp"
    ? "miniApp"
    : null;

const StoreCard = ({ item, count = 0, planBadge, onOpen }) => {
  const live = item.status !== "soon";
  const Tag = live ? "button" : "div";
  return (
    <Tag
      {...(live ? { type: "button", onClick: () => onOpen(item.open) } : { "aria-disabled": "true" })}
      className={`ssStore ${live ? "" : "ssStore--soon"} ${item.cover ? "ssStore--cover" : ""}`}
    >
      {item.cover && (
        <div className="ssStoreCover" aria-hidden="true">
          <img src={item.cover} alt="" loading="lazy" />
        </div>
      )}
      {count > 0 && (
        <span className="ssOwned" title={`${count} in your bots`} aria-label={`${count} in your bots`}>
          {count}
        </span>
      )}
      {live && planBadge && (
        <span className={`account-nav-badge ${planBadge.kind} ssUnlocked`}>{planBadge.label}</span>
      )}
      <div className={`ssStoreArt ${item.glyph ? "ssStoreArt--glyph" : ""}`}>
        <Art src={item.glyph || item.image} icon={item.icon} className="ssStoreGlyph" />
      </div>
      <div className="ssStoreInfo">
        <div className="ssStoreMeta">
          {item.status === "soon" && <span className="ssStatus">Coming soon</span>}
          <span className="ssStoreKicker">{item.kicker}</span>
        </div>
        <span className="ssStoreName">{item.name}</span>
        <span className="ssStoreDesc">{item.description}</span>
        {live && (
          <span className="altBtn altBtn--petrol ssStoreCta">
            {item.cta || `Create ${item.name}`}
            <i className="fa-solid fa-arrow-right" />
          </span>
        )}
      </div>
    </Tag>
  );
};

const AboutPlays = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div className="ssAbout" onClick={onClose}>
      <div
        className="ssAboutCard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ss-about-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ssAboutHead">
          <h2 id="ss-about-title">How bots work</h2>
          <button type="button" className="ssAboutClose" onClick={onClose} aria-label="Close">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <p>
          Send value, not just words. Every play is a tag you drop into an email, rendered per contact from the
          research and data you already store.
        </p>
        <ul>
          <li>Pick a bot in the Bot Store and set it up once.</li>
          <li>Copy its tag into the email.</li>
          <li>Each contact gets their own version when the email goes out.</li>
        </ul>
      </div>
    </div>,
    document.body,
  );
};

const Crumb = ({ current, onBack }) => (
  <nav className="ssCrumb" aria-label="Breadcrumb">
    <button type="button" className="ssCrumbBack" onClick={onBack}>
      <i className="fa-solid fa-arrow-left" />
      Sales Bots
    </button>
    <span className="ssCrumbSep">/</span>
    <span className="ssCrumbCurrent">{current}</span>
  </nav>
);

const scrollAncestorsToTop = (node) => {
  for (let el = node?.parentElement; el; el = el.parentElement) {
    if (el.scrollTop > 0) el.scrollTop = 0;
  }
  if (window.scrollY > 0) window.scrollTo(0, 0);
};

const SalesStudio = ({ userId, initialView = null }) => {
  const { researchLoading, planId } = useResearchAccess();
  const planBadge = !researchLoading && planId ? PLAN_NAV_BADGE[planId] : null;
  const [view, setView] = useState({ kind: "overview" });
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [about, setAbout] = useState(false);
  const topRef = useRef(null);
  const firstView = useRef(true);

  useLayoutEffect(() => {
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    scrollAncestorsToTop(topRef.current);
  }, [view]);

  useEffect(() => {
    if (!userId) return undefined;
    const unsub = onValue(
      ref(database, `/World/${userId}/settings/imageLab`),
      (snap) => {
        const node = snap.exists() ? snap.val() : null;
        setAssets(node && typeof node === "object" ? Object.entries(node).map(([id, v]) => ({ id, ...v })) : []);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [userId]);

  const plays = useMemo(() => buildPlays(assets), [assets]);
  const counts = useMemo(() => {
    const c = { all: plays.length };
    plays.forEach((p) => { c[p.kind] = (c[p.kind] || 0) + 1; });
    return c;
  }, [plays]);
  const visible = filter === "all" ? plays : plays.filter((p) => p.kind === filter);

  const back = () => setView({ kind: "overview" });

  if (view.kind === "lab") {
    return (
      <>
        <span ref={topRef} hidden />
        <Crumb current={LAB_NAMES[view.tool] || "AI Image"} onBack={back} />
        <ImageLab
          key={`${view.tool || "ai"}:${view.assetId || "new"}`}
          userId={userId}
          tool={view.tool || "ai"}
          focusAssetId={view.assetId || null}
          onExit={back}
        />
      </>
    );
  }

  if (view.kind === "miniApp") {
    return (
      <>
        <span ref={topRef} hidden />
        <Crumb current="Mini-App" onBack={back} />
        <MiniAppEditor
          key={view.assetId || "new_miniApp"}
          userId={userId}
          assetId={view.assetId}
          assets={assets}
          onExit={back}
        />
      </>
    );
  }

  if (view.kind === "play") {
    return (
      <>
        <span ref={topRef} hidden />
        <Crumb current={SALES_PLAY_CONFIGS[view.playType].name} onBack={back} />
        <BrandPlayEditor
          key={view.groupId || `new_${view.playType}`}
          playType={view.playType}
          userId={userId}
          groupId={view.groupId}
          assets={assets}
          onExit={back}
        />
      </>
    );
  }

  const openPlay = (play) =>
    setView(
      play.kind === "miniApp"
        ? { kind: "miniApp", assetId: play.id }
        : SALES_PLAY_CONFIGS[play.kind]
        ? { kind: "play", playType: play.kind, groupId: play.id }
        : { kind: "lab", tool: play.kind === "static" ? "image" : "ai", assetId: play.id }
    );
  return (
    <div className="ss" ref={topRef}>
      <section className="ssSection">
        <div className="ssSectionHead">
          <span className="ssSectionLabel">Your bots</span>
          <button type="button" className="ssInfo" aria-label="About bots" onClick={() => setAbout(true)}>
            <i className="fa-solid fa-circle-info" />
          </button>
          <span className="ssCount" style={loading ? { visibility: 'hidden' } : undefined}>{plays.length}</span>
          {plays.length > 0 && (
            <div className="ssFilters" role="tablist" aria-label="Filter bots">
              {FILTERS.filter((f) => f.id === "all" || counts[f.id] > 0).map((f) => (
                <button
                  type="button"
                  key={f.id}
                  role="tab"
                  aria-selected={filter === f.id}
                  className={`ssFilter ${filter === f.id ? "active" : ""}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                  <span>{counts[f.id]}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <p className="ssLoading">Loading bots…</p>
        ) : plays.length === 0 ? (
          <div className="ssEmpty">
            <i className="fa-solid fa-layer-group" />
            <div>
              <strong>No bots yet</strong>
              <span>Pick one from the Bot Store below. Everything you build shows up here.</span>
            </div>
          </div>
        ) : (
          <div className="ssPlays">
            {visible.map((play) => (
              <PlayCard key={`${play.kind}_${play.id}`} play={play} onOpen={() => openPlay(play)} />
            ))}
          </div>
        )}
      </section>

      <section className="ssSection">
        <div className="ssSectionHead">
          <span className="ssSectionLabel">Bot Store</span>
          <span className="ssCount">{VISIBLE_STORE_PLAYS.length}</span>
        </div>
        <div className="ssStoreGrid">
          {VISIBLE_STORE_PLAYS.map((item) => (
            <StoreCard key={item.id} item={item} count={counts[storeKind(item)] || 0} planBadge={planBadge} onOpen={setView} />
          ))}
        </div>
      </section>
      {about && <AboutPlays onClose={() => setAbout(false)} />}
    </div>
  );
};

export default SalesStudio;
